import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Sparkles, Check, X, Star, Clock, Zap, Image as ImageIcon, Users,
  ShieldCheck, Headphones, Calendar, BarChart3, Bot, Layers,
  ArrowRight, Crown, Rocket, Loader2,
} from 'lucide-react';
import { ScrollCtaBanner } from '../components/ScrollCtaBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Presentational metadata layered on top of the live tier doc. Keyed by the
// canonical tier id so admins can rename / re-price a plan without touching
// this lookup. Tiers not found here fall back to sensible defaults — so a
// custom plan added via Admin → Pricing still renders cleanly.
const PRESENTATION = {
  free: {
    target: 'Try It Free',
    cta: 'Start free',
    icon: Sparkles,
    accent: 'zinc',
    framing: 'Test-drive PostToSocial — no credit card required.',
  },
  starter: {
    target: 'Individuals & Small Creators',
    cta: 'Get Started',
    icon: Rocket,
    accent: 'zinc',
    framing: 'Enough to power daily posting across all your platforms',
  },
  growth: {
    target: 'Growing Brands & Agencies',
    cta: 'Start Scaling Now',
    icon: Star,
    accent: 'indigo',
    framing: 'The sweet spot for brands posting 2–4× per day',
  },
  pro: {
    target: 'Power Users & Agencies',
    cta: 'Unlock Full Automation',
    icon: Crown,
    accent: 'violet',
    framing: 'Built for agencies running multiple brands at scale',
  },
};
const DEFAULT_PRESENTATION = {
  target: 'For your team',
  cta: 'Get Started',
  icon: Rocket,
  accent: 'zinc',
  framing: 'Pick this if it fits how much you post.',
};

const VALUE_BULLETS = [
  { icon: Clock, text: 'Save 10+ hours per week' },
  { icon: Zap, text: 'AI generates images + captions instantly' },
  { icon: Calendar, text: 'Post consistently without burnout' },
  { icon: Users, text: 'Scale your content without hiring a team' },
];

// Comparison rows. The "Monthly Image Limit" row is rebuilt dynamically from
// each tier's `ai_images_per_month` value; the rest stay static. Tier-keyed
// values use the canonical ids (starter/growth/pro) — for any other custom
// tier the row falls back to '—'.
const STATIC_COMPARISON_ROWS = [
  { label: 'AI Post Generation', s: true, g: true, p: true },
  { label: 'Scheduling', s: true, g: true, p: true },
  { label: 'Auto Posting', s: true, g: true, p: true },
  { label: 'AI Auto-Scheduler', s: false, g: true, p: true },
  { label: 'AI Post Manager (Campaigns)', s: false, g: false, p: true },
  { label: 'Priority Queue', s: false, g: 'Standard', p: 'Highest' },
  { label: 'Analytics Level', s: 'Basic', g: 'Advanced', p: 'Advanced + Team' },
  { label: 'Multi-Account Management', s: false, g: true, p: true },
  { label: 'Team Collaboration', s: false, g: false, p: 'Coming Soon' },
  { label: 'Support Level', s: 'Email', g: 'Priority Email', p: 'Premium' },
];

const TRUST_BULLETS = [
  { icon: ShieldCheck, text: 'No contracts — cancel anytime' },
  { icon: ShieldCheck, text: 'Secure payments via Stripe' },
  { icon: Zap, text: 'Instant access after signup' },
  { icon: BarChart3, text: 'Built for real-world social media growth' },
];

const FAQS = [
  {
    q: 'What happens if I reach my image limit?',
    a: 'Your text and scheduling features keep working. Image generation pauses for the rest of the billing cycle, and you can upgrade in one click to unlock more — no service interruption.',
  },
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes. Plan changes take effect immediately and you\'re only charged the prorated difference. Downgrades apply at the end of the current period so you keep what you paid for.',
  },
  {
    q: 'Does it support multiple social accounts?',
    a: 'Starter supports the basics; Growth and Pro include Multi-Account Management for connecting multiple Facebook Pages, Instagram accounts, LinkedIn pages, Bluesky handles, and YouTube channels — all bookable from one calendar.',
  },
  {
    q: 'Is everything fully automated?',
    a: 'On Pro, yes — the AI Post Manager drafts a full campaign across all your platforms, picks optimal posting times, and queues everything for one-click approval. On Starter and Growth, you stay in the loop on every post.',
  },
  {
    q: 'Do I need design or marketing skills?',
    a: 'Not at all. The AI handles captions, hashtags, image prompts, and platform-specific tuning. You give it a topic, it gives you publish-ready content.',
  },
];

// Build the in-page plan objects from raw tier docs returned by the API.
// Layers in presentational meta + decides which plan to highlight.
// Includes Free (price=0) as a card — only strips out is_trial legacy plans
// and admin-archived plans. No artificial cap on count: if admin adds 4+
// plans, they all show (grid auto-adapts).
const buildPlans = (rawTiers) => {
  const visible = (rawTiers || [])
    .filter((t) => !t.is_trial && !t.archived)
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
  if (!visible.length) return [];
  const popularExists = visible.some((t) => t.is_popular);
  return visible.map((t, idx) => {
    const meta = PRESENTATION[t.id] || DEFAULT_PRESENTATION;
    // Default "Most Popular" highlight: the cheapest PAID plan in the middle
    // of the paid set — falls back to overall middle if no paid plans.
    const paidIdxs = visible
      .map((p, i) => ((p.price ?? 0) > 0 ? i : null))
      .filter((i) => i !== null);
    const middlePaidIdx = paidIdxs.length
      ? paidIdxs[Math.floor(paidIdxs.length / 2)]
      : Math.floor(visible.length / 2);
    const isHighlight = popularExists ? !!t.is_popular : idx === middlePaidIdx;
    const isFree = (t.price ?? 0) === 0;
    return {
      id: t.id,
      name: t.name,
      price: t.price,
      is_free: isFree,
      ai_images_per_month: t.ai_images_per_month,
      features: t.features || [],
      // Promotional discount (server-decorated)
      discount_active: !!t.discount_active,
      discount_percent: t.discount_percent || 0,
      discount_label: t.discount_label || null,
      discounted_price: t.discounted_price ?? null,
      // Promo trial ($1 / N days) — eligibility is decided per-user at render time
      promo_trial_enabled: !!t.promo_trial_enabled,
      promo_trial_price: t.promo_trial_price,
      promo_trial_days: t.promo_trial_days,
      ...meta,
      // Free plan gets a simpler CTA; paid plans keep their custom CTA.
      cta: isFree ? 'Start free' : meta.cta,
      badge: isHighlight && !isFree ? 'Most Popular' : null,
      highlight: isHighlight && !isFree,
    };
  });
};

export const PricingPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibleForPromo, setEligibleForPromo] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/subscription/tiers`);
        if (cancelled) return;
        setPlans(buildPlans(r.data?.tiers));
        // For signed-in users, check eligibility via /subscription/current
        if (user && api) {
          try {
            const cur = await api.get('/subscription/current');
            if (!cancelled) setEligibleForPromo(!!cur.data?.eligible_for_promo_trial);
          } catch (_) { /* anonymous fallback */ }
        }
      } catch (_) {
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, api]);

  const handleCta = (planId) => {
    if (user) {
      navigate(`/subscription?plan=${planId}`);
    } else {
      navigate(`/register?plan=${planId}`);
    }
  };

  // Paid-only subset for the feature comparison table + closing CTA. The
  // table compares feature depth (which Free doesn't fit cleanly into) and
  // the closing CTA pushes a paid plan as the conversion goal.
  const paidPlans = plans.filter((p) => !p.is_free);
  const ctaPlan = paidPlans.find((p) => p.highlight) || paidPlans[0] || null;

  // Resolve a static comparison-row value for a given plan id. The static
  // rows are keyed by s/g/p — for any custom plan we degrade to '—'.
  const cellFor = (row, planId) => {
    if (planId === 'starter') return row.s;
    if (planId === 'growth') return row.g;
    if (planId === 'pro') return row.p;
    return '—';
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="pricing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-200/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight">PostToSocial</span>
            </Link>
            <div className="flex items-center gap-3">
              {user ? (
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-zinc-600" data-testid="pricing-nav-dashboard">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-zinc-600" data-testid="pricing-nav-login">
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="pricing-nav-register">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8" data-testid="pricing-hero">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 mb-5">
            <Bot className="w-3.5 h-3.5 mr-1.5" />
            Pricing built for creators who post daily
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 leading-[1.05]">
            Stop Creating Content Manually.
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 bg-clip-text text-transparent">
              Let AI Do It For You.
            </span>
          </h1>
          <p className="mt-5 text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            Generate, schedule, and post high-performing content across all your platforms — automatically.
          </p>

          {/* Value bullets */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {VALUE_BULLETS.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-zinc-200 text-sm text-zinc-700"
                data-testid={`value-bullet-${i}`}
              >
                <Icon className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <span className="text-left leading-tight">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-testid="pricing-cards">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              No plans available right now — please check back soon.
            </div>
          ) : (
          <div
            className={
              // Grid adapts to plan count — 1/2 = centered, 3 = lg:cols-3, 4+ = lg:cols-4
              plans.length === 1
                ? 'grid grid-cols-1 max-w-md mx-auto items-stretch'
                : plans.length === 2
                  ? 'grid sm:grid-cols-2 max-w-3xl mx-auto gap-6 lg:gap-8 items-stretch'
                  : plans.length === 3
                    ? 'grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch'
                    : 'grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch'
            }
            data-testid={`pricing-grid-count-${plans.length}`}
          >
            {plans.map((plan) => {
              const Icon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={`relative ${plan.highlight ? 'lg:-my-4 z-10' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                      <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white border-0 px-3 py-1 text-xs font-semibold shadow-md">
                        <Star className="w-3 h-3 mr-1 fill-white" />
                        {plan.badge}
                      </Badge>
                    </div>
                  )}
                  <Card
                    className={`h-full transition-all ${
                      plan.highlight
                        ? 'border-2 border-indigo-500 shadow-xl shadow-indigo-100/60 bg-gradient-to-b from-white to-indigo-50/30'
                        : 'border-zinc-200 hover:border-zinc-300 hover:shadow-md'
                    }`}
                  >
                    <CardContent className="p-6 sm:p-7 flex flex-col h-full">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-1">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            plan.highlight
                              ? 'bg-indigo-600 text-white'
                              : plan.accent === 'violet'
                              ? 'bg-violet-100 text-violet-600'
                              : 'bg-zinc-100 text-zinc-700'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="text-xl font-bold text-zinc-900">{plan.name}</h3>
                      </div>
                      <p className="text-xs text-zinc-500 mb-5">{plan.target}</p>

                      {/* Price */}
                      <div className="mb-1.5">
                        {/* $1 promo-trial badge — shown above the price when eligible.
                            Stacks with discount when both apply (we only use the badge,
                            the discount is suppressed at checkout per backend rules). */}
                        {!plan.is_free && plan.promo_trial_enabled && eligibleForPromo && (
                          <div
                            className="mb-2 inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 text-[11px] font-semibold px-2 py-1 rounded-full"
                            data-testid={`plan-${plan.id}-promo-trial-badge`}
                          >
                            🎉 First {plan.promo_trial_days} days for ${Number(plan.promo_trial_price).toFixed(0)}
                          </div>
                        )}
                        {plan.is_free ? (
                          <>
                            <span className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                              Free
                            </span>
                            <span className="text-zinc-500 ml-1.5 text-base">forever</span>
                          </>
                        ) : plan.discount_active && plan.discounted_price != null ? (
                          <>
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-zinc-400 text-xl line-through" data-testid={`plan-${plan.id}-original-price`}>
                                ${Number(plan.price).toFixed(0)}
                              </span>
                              <span className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900" data-testid={`plan-${plan.id}-discounted-price`}>
                                ${Number(plan.discounted_price).toFixed(2)}
                              </span>
                              <span className="text-zinc-500 text-base">/month</span>
                            </div>
                            <p
                              className="text-[11px] text-emerald-700 font-semibold mt-1"
                              data-testid={`plan-${plan.id}-discount-caption`}
                            >
                              Save {Math.round(plan.discount_percent)}%
                              {plan.discount_label ? ` — ${plan.discount_label}` : ' — limited time'}
                            </p>
                          </>
                        ) : (
                          <>
                            <span className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                              ${Number(plan.price).toFixed(0)}
                            </span>
                            <span className="text-zinc-500 ml-1.5 text-base">/month</span>
                          </>
                        )}
                      </div>
                      {plan.highlight ? (
                        <p className="text-[11px] text-indigo-600 font-medium mb-3">
                          Less than ${Math.max(1, Math.round(plan.price / 30))}/day to automate your entire content system
                        </p>
                      ) : (
                        <p className="text-[11px] text-zinc-500 mb-3">{plan.framing}</p>
                      )}

                      {/* CTA */}
                      <Button
                        onClick={() => handleCta(plan.id)}
                        className={`w-full mb-6 h-11 font-semibold ${
                          plan.highlight
                            ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-200/60'
                            : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                        }`}
                        data-testid={`plan-cta-${plan.id}`}
                      >
                        {plan.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>

                      {/* Features */}
                      <ul className="space-y-2.5 flex-1">
                        {plan.features.map((feat, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2.5 text-sm text-zinc-700"
                            data-testid={`plan-${plan.id}-feature-${i}`}
                          >
                            <Check
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                plan.highlight ? 'text-indigo-600' : 'text-emerald-600'
                              }`}
                            />
                            <span className="leading-snug">{feat}</span>
                          </li>
                        ))}
                      </ul>

                      <p className="mt-6 pt-4 border-t border-zinc-100 text-[11px] text-zinc-400 italic">
                        If you need more, you can upgrade anytime.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Comparison table — paid plans only (Free excluded; it's covered by
          the card above and doesn't fit the feature-depth matrix). */}
      {paidPlans.length >= 2 && (
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white border-y border-zinc-200" data-testid="pricing-comparison">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900 mb-2">
              Compare plans, side by side
            </h2>
            <p className="text-zinc-600 text-base">Pick the plan that matches your posting cadence.</p>
          </div>
          {/* Desktop table */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-zinc-200">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left text-sm font-semibold text-zinc-900 px-6 py-4 w-2/5">Features</th>
                  {paidPlans.map((p) => (
                    <th
                      key={p.id}
                      className={`text-center text-sm px-4 py-4 ${
                        p.highlight
                          ? 'font-bold text-indigo-700 bg-indigo-50/50 border-x border-indigo-100'
                          : 'font-semibold text-zinc-700'
                      }`}
                    >
                      {p.name}
                      <div className={`text-xs font-normal ${p.highlight ? 'text-indigo-600 font-medium' : 'text-zinc-500'}`}>
                        ${Number(p.price).toFixed(0)}/mo
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {/* Dynamic "Monthly Image Limit" row driven by each tier's ai_images_per_month */}
                <tr className="hover:bg-zinc-50/50">
                  <td className="text-sm text-zinc-700 px-6 py-3.5">Monthly Image Limit</td>
                  {paidPlans.map((p) => (
                    <ComparisonCell
                      key={p.id}
                      value={p.ai_images_per_month != null ? p.ai_images_per_month.toLocaleString() : '—'}
                      highlighted={p.highlight}
                    />
                  ))}
                </tr>
                {STATIC_COMPARISON_ROWS.map((row, i) => (
                  <tr key={i} className="hover:bg-zinc-50/50">
                    <td className="text-sm text-zinc-700 px-6 py-3.5">{row.label}</td>
                    {paidPlans.map((p) => (
                      <ComparisonCell key={p.id} value={cellFor(row, p.id)} highlighted={p.highlight} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile stacked summary */}
          <div className="md:hidden space-y-4">
            {paidPlans.map((plan) => (
              <div key={plan.id} className="rounded-lg border border-zinc-200 p-4">
                <div className="font-semibold text-zinc-900 mb-2">
                  {plan.name} — <span className="text-indigo-600">${Number(plan.price).toFixed(0)}/mo</span>
                </div>
                <ul className="space-y-1.5">
                  <li className="flex justify-between text-xs">
                    <span className="text-zinc-600">Monthly Image Limit</span>
                    <span className="text-zinc-900 font-medium">
                      {plan.ai_images_per_month != null ? plan.ai_images_per_month.toLocaleString() : '—'}
                    </span>
                  </li>
                  {STATIC_COMPARISON_ROWS.map((row, i) => {
                    const v = cellFor(row, plan.id);
                    return (
                      <li key={i} className="flex justify-between text-xs">
                        <span className="text-zinc-600">{row.label}</span>
                        <span className="text-zinc-900 font-medium">
                          {typeof v === 'boolean' ? (v ? 'Included' : '—') : v}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Trust */}
      <section className="px-4 sm:px-6 lg:px-8 py-16" data-testid="pricing-trust">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 mb-3">
            Built for Creators, Brands, and Agencies
          </h2>
          <p className="text-zinc-600 mb-10 max-w-2xl mx-auto">
            Every plan ships with the operational essentials you'd expect from a serious tool.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST_BULLETS.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-2.5 p-5 bg-white rounded-xl border border-zinc-200"
                data-testid={`trust-bullet-${i}`}
              >
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-sm text-zinc-700 font-medium leading-snug">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 mb-2 text-center">
            Frequently asked questions
          </h2>
          <p className="text-zinc-600 mb-10 text-center">
            Quick answers to the things people usually ask first.
          </p>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-lg border border-zinc-200 px-5 [&[data-state=open]]:border-indigo-300"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-zinc-900 hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing CTA */}
      {ctaPlan && (
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-testid="pricing-closing-cta">
        <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-pink-600 p-10 sm:p-14 text-center shadow-xl">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
            Ready to ship content on autopilot?
          </h3>
          <p className="text-indigo-100 text-base sm:text-lg mb-7 max-w-xl mx-auto">
            Start with {ctaPlan.name} — most teams find it pays for itself within the first week.
          </p>
          <Button
            size="lg"
            onClick={() => handleCta(ctaPlan.id)}
            className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold h-12 px-8"
            data-testid="closing-cta-btn"
          >
            Start with {ctaPlan.name} —{' '}
            {ctaPlan.discount_active && ctaPlan.discounted_price != null
              ? `$${Number(ctaPlan.discounted_price).toFixed(2)}/mo (save ${Math.round(ctaPlan.discount_percent)}%)`
              : `$${Number(ctaPlan.price).toFixed(0)}/mo`}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-xs text-indigo-200 mt-4">No contracts • Cancel anytime • Instant access</p>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-zinc-500">© {new Date().getFullYear()} PostToSocial. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-zinc-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-900 transition-colors">Terms</Link>
            <Link to="/data-deletion" className="hover:text-zinc-900 transition-colors">Data Deletion</Link>
          </div>
        </div>
      </footer>
      <ScrollCtaBanner />
    </div>
  );
};

const ComparisonCell = ({ value, highlighted = false }) => {
  const baseClasses = `text-center text-sm px-4 py-3.5 ${
    highlighted ? 'bg-indigo-50/40 border-x border-indigo-100' : ''
  }`;
  if (typeof value === 'boolean') {
    return (
      <td className={baseClasses}>
        {value ? (
          <Check className="w-4 h-4 text-emerald-600 inline-block" />
        ) : (
          <X className="w-4 h-4 text-zinc-300 inline-block" />
        )}
      </td>
    );
  }
  return (
    <td className={`${baseClasses} text-zinc-700 font-medium`}>{value}</td>
  );
};

export default PricingPage;
